import React, { forwardRef, useImperativeHandle, useRef } from "react";
import html2canvas from "html2canvas";
import logo from "../../assets/Logo_Prosperidad_Social.png";

const COLORES = {
  azul:    { fondo: "#e6f4ff", borde: "#91caff", texto: "#0958d9" },
  verde:   { fondo: "#f6ffed", borde: "#b7eb8f", texto: "#389e0d" },
  rosa:    { fondo: "#fff0f6", borde: "#ffadd2", texto: "#c41d7f" },
  naranja: { fondo: "#fff7e6", borde: "#ffd591", texto: "#d46b08" },
};

const ANCHO_PLANTILLA = 720; // px — ancho de contenido para cada captura individual

const Badge = ({ valor, positivo }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      backgroundColor: positivo ? "#f6ffed" : "#fff1f0",
      color: positivo ? "#389e0d" : "#cf1322",
      border: `1px solid ${positivo ? "#b7eb8f" : "#ffa39e"}`,
    }}
  >
    {valor}
  </span>
);

const Fila = ({ label, value }) => (
  <tr>
    <td
      style={{
        padding: "5px 10px",
        fontSize: 11,
        fontWeight: 600,
        color: "#595959",
        backgroundColor: "#fafafa",
        border: "1px solid #f0f0f0",
        width: "42%",
        verticalAlign: "top",
      }}
    >
      {label}
    </td>
    <td
      style={{
        padding: "5px 10px",
        fontSize: 11,
        color: "#262626",
        border: "1px solid #f0f0f0",
        wordBreak: "break-word",
      }}
    >
      {value ?? "—"}
    </td>
  </tr>
);

const formatFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  return isNaN(fecha.getTime()) ? valor : fecha.toLocaleString("es-CO");
};

const SinDatos = ({ texto }) => (
  <div style={{ padding: "20px 0", textAlign: "center", color: "#8c8c8c", fontSize: 12 }}>
    {texto}
  </div>
);

// ── Card individual con su propio contenedor (cada una se captura por separado) ──
const SeccionBase = ({ innerRef, titulo, color, children }) => {
  const c = COLORES[color];
  return (
    <div
      ref={innerRef}
      style={{
        position: "fixed",
        top: 0,
        left: -10000,
        width: ANCHO_PLANTILLA,
        backgroundColor: "#ffffff",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#262626",
      }}
    >
      <div
        style={{
          border: `1px solid ${c.borde}`,
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: c.fondo,
            color: c.texto,
            padding: "8px 14px",
            fontWeight: 700,
            fontSize: 13,
            borderBottom: `1px solid ${c.borde}`,
          }}
        >
          {titulo}
        </div>
        <div style={{ padding: 10, backgroundColor: "#ffffff" }}>{children}</div>
      </div>
    </div>
  );
};

const InformePrintTemplate = forwardRef(({ informe, kokamData }, ref) => {
  const headerRef   = useRef(null);
  const infoRef      = useRef(null);
  const seccion509Ref = useRef(null);
  const seccion573Ref = useRef(null);
  const seccion667Ref = useRef(null);
  const seccionKokamRef = useRef(null);
  const footerRef    = useRef(null);

  useImperativeHandle(ref, () => ({
    /**
     * Captura cada bloque (header, info, las 4 cards y footer) como canvases
     * independientes, para que el PDF pueda armar la paginación sin
     * cortar ninguna card a la mitad.
     */
    capturar: async () => {
      const opciones = { scale: 2, useCORS: true, backgroundColor: "#ffffff" };

      const capturarNodo = async (nodo) => {
        if (!nodo) return null;
        return html2canvas(nodo, opciones);
      };

      const headerCanvas = await capturarNodo(headerRef.current);
      const infoCanvas    = await capturarNodo(infoRef.current);
      const c509  = await capturarNodo(seccion509Ref.current);
      const c573  = await capturarNodo(seccion573Ref.current);
      const c667  = await capturarNodo(seccion667Ref.current);
      const cKokam = await capturarNodo(seccionKokamRef.current);
      const footerCanvas = await capturarNodo(footerRef.current);

      return {
        headerCanvas,
        infoCanvas,
        secciones: [c509, c573, c667, cKokam],
        footerCanvas,
      };
    },
  }));

  if (!informe) return null;

  const items509 = informe.encuesta_509 || [];
  const items573 = informe.encuesta_573 || [];
  const items667 = informe.encuesta_667 || [];
  const kokam     = kokamData || [];

  const fechaGeneracion = new Date().toLocaleString("es-CO");

  return (
    <>
      {/* ── Cabecera ── */}
      <div
        ref={headerRef}
        style={{
          position: "fixed",
          top: 0,
          left: -10000,
          width: ANCHO_PLANTILLA,
          backgroundColor: "#ffffff",
          fontFamily: "Arial, Helvetica, sans-serif",
          padding: "0 0 14px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #1B5E20",
            paddingBottom: 14,
          }}
        >
          <img src={logo} alt="logo" style={{ height: 56 }} />
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1B5E20" }}>
              INFORME CONSOLIDADO RIT
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>
              Caracterización, Entregas y Aprobación Kokam
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 10, color: "#8c8c8c", minWidth: 140 }}>
            <div>Generado: {fechaGeneracion}</div>
          </div>
        </div>
      </div>

      {/* ── Datos del sujeto ── */}
      <div
        ref={infoRef}
        style={{
          position: "fixed",
          top: 0,
          left: -10000,
          width: ANCHO_PLANTILLA,
          backgroundColor: "#fafafa",
          border: "1px solid #f0f0f0",
          borderRadius: 6,
          padding: "10px 16px",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: 12,
          color: "#262626",
        }}
      >
        <strong>Sujeto consultado (documento):</strong> {informe.sujetoid}
      </div>

      {/* ── Sección 1: Caracterización 509 ── */}
      <SeccionBase innerRef={seccion509Ref} titulo="REPORTE RIT 509 — Caracterización" color="azul">
        {items509.length === 0 ? (
          <SinDatos texto="Sin información de caracterización" />
        ) : (
          items509.map((item, idx) => (
            <table key={item.id ?? idx} style={{ width: "100%", borderCollapse: "collapse", marginBottom: idx < items509.length - 1 ? 14 : 0 }}>
              <tbody>
                <Fila label="Documento" value={item.documento} />
                <Fila label="Nombre titular" value={item.nombreTitular} />
                <Fila label="Fecha nacimiento" value={item.fechaNacimiento} />
                <Fila label="Celular" value={item.celular} />
                <Fila label="Correo" value={item.correo} />
                <Fila label="Dirección" value={item.direccion} />
                <Fila label="Barrio" value={item.barrio} />
                <Fila label="Dpto. atención" value={item.codDptoAtencion} />
                <Fila label="Municipio atención" value={item.codMunicipioAtencion} />
                <Fila label="Proyecto" value={item.nombreProyecto} />
                <Fila label="Desea participar" value={<Badge valor={item.deseaParticipar || "—"} positivo={String(item.deseaParticipar).toUpperCase() === "SI"} />} />
                <Fila label="Fecha diligenciamiento" value={formatFecha(item.fechaDiligenciamiento)} />
                <Fila label="Estado" value={<Badge valor={String(item.estado) === "1" ? "Activo" : "Inactivo"} positivo={String(item.estado) === "1"} />} />
                <Fila label="Fecha enviado" value={item.fechaEnviado} />
              </tbody>
            </table>
          ))
        )}
      </SeccionBase>

      {/* ── Sección 2: Entregas 1 - 573 ── */}
      <SeccionBase innerRef={seccion573Ref} titulo="Entregas 1 — Encuesta 573" color="verde">
        {items573.length === 0 ? (
          <SinDatos texto="Sin información de entrega" />
        ) : (
          items573.map((item, idx) => (
            <table key={item.id ?? idx} style={{ width: "100%", borderCollapse: "collapse", marginBottom: idx < items573.length - 1 ? 14 : 0 }}>
              <tbody>
                <Fila label="N° documento titular" value={item.numeroDocumentoTitular} />
                <Fila label="N° entrega" value={item.nroEntrega} />
                <Fila label="Nombre completo" value={item.nombreCompleto || item.nombreTitular} />
                <Fila label="Quién reclama" value={item.quienReclamaBeneficio} />
                <Fila label="Departamento" value={item.departamento} />
                <Fila label="Municipio" value={item.municipio} />
                <Fila label="Punto de entrega" value={item.puntoEntrega} />
                <Fila label="Proyecto" value={item.nombreProyecto} />
                <Fila label="Fecha diligenciamiento" value={formatFecha(item.fechaDiligenciamiento)} />
                <Fila label="Estado" value={<Badge valor={String(item.estado) === "1" ? "Activo" : "Inactivo"} positivo={String(item.estado) === "1"} />} />
                <Fila label="Fecha enviado" value={item.fechaEnviado} />
              </tbody>
            </table>
          ))
        )}
      </SeccionBase>

      {/* ── Sección 3: Entregas 2 - 667 ── */}
      <SeccionBase innerRef={seccion667Ref} titulo="Entregas 2 — Encuesta 667" color="rosa">
        {items667.length === 0 ? (
          <SinDatos texto="Sin información de entrega" />
        ) : (
          items667.map((item, idx) => (
            <table key={item.id ?? idx} style={{ width: "100%", borderCollapse: "collapse", marginBottom: idx < items667.length - 1 ? 14 : 0 }}>
              <tbody>
                <Fila label="N° documento titular" value={item.numeroDocumentoTitular} />
                <Fila label="N° entrega" value={item.nroEntrega} />
                <Fila label="Nombre completo" value={item.nombreCompleto || item.nombreTitular} />
                <Fila label="Quién reclama" value={item.quienReclamaBeneficio} />
                <Fila label="Departamento" value={item.departamento} />
                <Fila label="Municipio" value={item.municipio} />
                <Fila label="Punto de entrega" value={item.puntoEntrega} />
                <Fila label="Proyecto" value={item.nombreProyecto} />
                <Fila label="Fecha diligenciamiento" value={formatFecha(item.fechaDiligenciamiento)} />
                <Fila label="Estado" value={<Badge valor={String(item.estado) === "1" ? "Activo" : "Inactivo"} positivo={String(item.estado) === "1"} />} />
                <Fila label="Fecha enviado" value={item.fechaEnviado} />
              </tbody>
            </table>
          ))
        )}
      </SeccionBase>

      {/* ── Sección 4: Reporte Kokam ── */}
      <SeccionBase innerRef={seccionKokamRef} titulo="Reporte Kokam — Aprobados" color="naranja">
        {kokam.length === 0 ? (
          <SinDatos texto="Sin información en el reporte Kokam" />
        ) : (
          kokam.map((item, idx) => (
            <table key={`${item.identificacion}-${idx}`} style={{ width: "100%", borderCollapse: "collapse", marginBottom: idx < kokam.length - 1 ? 14 : 0 }}>
              <tbody>
                <Fila label="Identificación" value={item.identificacion} />
                <Fila label="Nombre completo" value={item.nombreCompleto} />
                <Fila label="Municipio" value={item.municipioParticipante} />
                <Fila label="Departamento" value={item.deptoParticipante} />
                <Fila label="Promotor" value={[item.nombresPromotor, item.apellidosPromotor].filter(Boolean).join(" ")} />
                <Fila label="Cédula promotor" value={item.cedulaPromotor} />
                <Fila label="Teléfono" value={item.telefono} />
                <Fila label="Dirección" value={item.direccion} />
                <Fila label="Aprobación pago" value={<Badge valor={item.aprobacionPago || "—"} positivo={String(item.aprobacionPago).toUpperCase() === "SI"} />} />
              </tbody>
            </table>
          ))
        )}
      </SeccionBase>

      {/* ── Pie de página ── */}
      <div
        ref={footerRef}
        style={{
          position: "fixed",
          top: 0,
          left: -10000,
          width: ANCHO_PLANTILLA,
          backgroundColor: "#ffffff",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: 9,
          color: "#bfbfbf",
          textAlign: "center",
          padding: "10px 0 0 0",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        Documento generado automáticamente por el sistema 724 — Prosperidad Social
      </div>
    </>
  );
});

export default InformePrintTemplate;